import React from 'react'

import update from 'immutability-helper'

import axios from 'axios'
import { bool, func, string } from 'prop-types'

import Loader from 'Pods/Shared/Loader'

import MainContent from './Components/MainContent'

class ActionCableMessaging extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      userId: 0,
      meta: {
        currentPage: 0,
        totalPages: 0,
      },
      isLoadingMore: false,
      messages: [],
      included: [],
      scrollBottomOffset: 0,
    }

    this.onFetchSuccess = this.onFetchSuccess.bind(this)
    this.onFetchFailure = this.onFetchFailure.bind(this)
    this.onLoadMore = this.onLoadMore.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.markMessageAsRead = this.markMessageAsRead.bind(this)
  }

  componentDidMount() {
    const { conversationId } = this.props

    this.cancelSource = axios.CancelToken.source()

    this.fetchData()

    this.setState({ userId: App.config.userId })

    if (App.cable?.subscriptions) {
      const channelConfig = {
        channel: 'ConversationsChannel',
        conversation_id: conversationId,
      }

      this.channel = App.cable.subscriptions.create(channelConfig, {
        connected() {},
        received: (data) => {
          const isPriceOffer = data.type === 'price-offers/conversation/create-update'
          const userRelationship = data.data.data.relationships.user
          const isNotSender = userRelationship && `${App.config.userId}` !== userRelationship.data.id

          if (isPriceOffer || isNotSender) {
            this.handleDataReceived(data)
          }
        },
      })
    }
  }

  componentWillUnmount() {
    if (this.cancelSource) {
      this.cancelSource.cancel()
    }
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }

  static captureException(error) {
    // Silently ignore canceled requests - expected on navigation/unmount
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
      return
    }

    if (window.Sentry && typeof window.Sentry.captureException === 'function') {
      window.Sentry.captureException(error)
    }
  }

  handleDataReceived(data) {
    const { updateOffer, updateAttachments } = this.props
    const newMessage = data.data.data

    if (newMessage.attributes.type === 'offer') {
      return updateOffer(newMessage.attributes.typeData)
    }

    if (data.type === 'conversations/system-message/create') {
      newMessage.attributes.date = Math.round(+new Date() / 1000)
      newMessage.relationships = {}
      newMessage.relationships.attachments = { data: [] }
      newMessage.relationships.user = {}

      this.setState((prevState) => ({
        messages: [...prevState.messages, newMessage],
      }))
    } else {
      const attachments = data.data.included.filter((incl) => incl.type === 'attachments')

      newMessage.attributes.isReceived = true

      this.setState((prevState) => ({
        messages: [...prevState.messages, newMessage],
        included: [...data.data.included, ...prevState.included],
      }))

      if (attachments.length > 0) {
        updateAttachments(attachments)
      }
    }

    this.markMessageAsRead()
  }

  markMessageAsRead() {
    const { conversationId } = this.props
    const { userId } = this.state

    axios.put(`/api/internal/conversations/${conversationId}/mark-read`, { user_id: userId })
      .catch((error) => ActionCableMessaging.captureException(error))
  }

  fetchData() {
    const { conversationId } = this.props
    const { meta } = this.state

    const config = {
      params: {
        page: Number(meta.currentPage || 0) + 1,
      },
      cancelToken: this.cancelSource.token,
    }

    axios.get(`/api/internal/conversations/${conversationId}/messages`, config)
      .then(this.onFetchSuccess)
      .catch(this.onFetchFailure)
  }

  onFetchSuccess(response) {
    this.setState((prevState) => ({
      messages: [...(response.data.data.reverse()), ...prevState.messages],
      included: [...response.data.included, ...prevState.included],
      meta: response.data.meta,
      isLoadingMore: false,
    }))
  }

  onFetchFailure(error) {
    ActionCableMessaging.captureException(error)

    this.setState({
      isLoadingMore: false,
    })
  }

  onLoadMore(messagesScrollBottomOffset) {
    this.setState({
      scrollBottomOffset: messagesScrollBottomOffset,
      isLoadingMore: true,
    })

    this.fetchData()
  }

  onSubmit(response) {
    const { updateAttachments } = this.props

    this.setState((prevState) => ({
      included: [...response.data.included, ...prevState.included],
      messages: update(prevState.messages, { $push: [response.data.data] }),
      scrollBottomOffset: 0,
    }))

    const attachments = response.data.included.filter((incl) => incl.type === 'attachments')
    updateAttachments(attachments)
  }

  render() {
    const {
      conversationId,
      conversationType,
      isJobConversation,
    } = this.props
    const {
      messages,
      included,
      meta,
      scrollBottomOffset,
      isLoadingMore,
      userId,
    } = this.state

    if (messages.length === 0) {
      return (
        <Loader />
      )
    }

    return (
      <MainContent
        conversationId={conversationId}
        conversationType={conversationType}
        messages={messages}
        included={included}
        meta={meta}
        scrollBottomOffset={scrollBottomOffset}
        onLoadMore={this.onLoadMore}
        isLoadingMore={isLoadingMore}
        onSubmit={this.onSubmit}
        isSuspendedMessaging={false}
        isAttachmentsHidden={false}
        isLiveMessaging
        userId={userId}
        isJobConversation={isJobConversation}
      />
    )
  }
}

ActionCableMessaging.propTypes = {
  conversationId: string.isRequired,
  updateAttachments: func,
  updateOffer: func,
  isJobConversation: bool,
  conversationType: string.isRequired,
}

ActionCableMessaging.defaultProps = {
  updateAttachments: () => {},
  updateOffer: () => {},
  isJobConversation: false,
}

export default ActionCableMessaging
